// Recipes-tab body for the Inventory panel. Adds a "Find duplicates"
// toolbar above the existing read-only recipe inventory list. Pulled
// out of the orchestrator (with the materials-tab body) so the panel
// file stays under the file-size hygiene cap.
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgRecipeInventoryRow, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { ManufacturingDuplicateFinderPanel } from "./ManufacturingDuplicateFinderPanel";
import { ManufacturingRecipeDuplicateRow } from "./ManufacturingRecipeDuplicateRow";
import { RecipeInventoryBody } from "./RecipeInventoryBody";

type AsyncResult = { ok: true } | { ok: false; error: string };

type Props = {
  recipes: ReadonlyArray<MfgRecipeSummary>;
  inventoryRows: ReadonlyArray<MfgRecipeInventoryRow>;
  totalInventoryValue: number;
  loading: boolean;
  error: string | null;
  gateEnabled: boolean;
  busy: boolean;
  duplicatesOpen: boolean;
  onToggleDuplicates: () => void;
  onCloseDuplicates: () => void;
  onRenameRecipe: (recipeId: string, nextName: string) => Promise<AsyncResult>;
  onDeactivateRecipe: (recipeId: string) => Promise<AsyncResult>;
  canDeactivateRecipe?: boolean;
};

export function ManufacturingInventoryRecipesTabBody(props: Props) {
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          type="button"
          className={ghostBtnClass}
          onClick={props.onToggleDuplicates}
          disabled={!props.gateEnabled || props.recipes.length === 0}
          aria-pressed={props.duplicatesOpen}
          title="Group recipes whose name matches"
        >
          {props.duplicatesOpen ? "Hide duplicates" : "Find duplicates"}
        </button>
      </div>
      {props.duplicatesOpen ? (
        <ManufacturingDuplicateFinderPanel<MfgRecipeSummary>
          items={props.recipes}
          getName={(r) => r.name}
          getKey={(r) => r.id}
          onClose={props.onCloseDuplicates}
          entityPlural="recipes"
          renderRow={(recipe) => (
            <ManufacturingRecipeDuplicateRow
              recipe={recipe}
              busy={props.busy}
              canDeactivate={props.canDeactivateRecipe}
              onRename={props.onRenameRecipe}
              onDeactivate={props.onDeactivateRecipe}
            />
          )}
        />
      ) : null}
      <RecipeInventoryBody
        rows={props.inventoryRows}
        totalInventoryValue={props.totalInventoryValue}
        loading={props.loading}
        error={props.error}
      />
    </div>
  );
}
